import { Component, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import { AuthLayoutComponent, FormInputComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { LoginResponse } from "../../../shared/models";
import { LoaderService, LoginService, UserProfileApiService } from "../../../shared/services";

@Component({
  selector: "adaa-link-account",
  imports: [TranslatePipe, RouterLink, ReactiveFormsModule, AuthLayoutComponent, FormInputComponent],
  templateUrl: "./link-account.html",
  styleUrl: "./link-account.scss",
})
export class LinkAccountComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _loginService = inject(LoginService);
  private readonly _userProfileApiService = inject(UserProfileApiService);

  readonly uuid = input.required<string>({ alias: "uuid" });

  readonly step = signal<number>(1);
  readonly linkAccountForm = signal<FormGroup | undefined>(undefined);
  readonly showPassword = signal<boolean>(false);

  public ngOnInit(): void {
    this._prepareForm();
  }

  public linkAccount(isConfirmed: boolean): void {
    if (isConfirmed) {
      this.step.set(2);
    } else {
      this._loaderService.start();
      this._toastrService.error(this._translateService.instant("notification.error.code_67"), undefined, {
        timeOut: 10000,
      });
      this._loginService.uaePassLogout();
    }
  }

  public submit(): void {
    const form = this.linkAccountForm();
    if (!form?.valid) return;

    this._loginService.uaePassLinkAccount(form.getRawValue(), this.uuid()).subscribe({
      next: (response) => {
        if (response.inError) return;

        AdaaHelper.setLocalStorage(Constants.localStorageKeys.uaePassLogin, true, "string");
        this._onSuccessfulLogin(response.responseData);
      },
    });
  }

  private _prepareForm() {
    this.linkAccountForm.set(
      this._formBuilder.group({
        email: [null, [Validators.required, Validators.email]],
        password: [null, [Validators.required, noWhitespaceValidator()]],
      })
    );
  }

  private _onSuccessfulLogin(user: LoginResponse) {
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.sessionId, user.sessionId, "string");
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentEntity, { id: user.entityId }, "json");

    //Used after login to check page
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.checkLogin, "1", "string");

    //User to get the current user info in the system
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.user, user, "json");

    const clearRelevantStorage = () => {
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.firstVisit);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.lastUser);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.lastUrlVisited);
      AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.systemTour);
    };

    this._userProfileApiService.getSecurityQuestion().subscribe({
      next: (response) => {
        if (response.inError) {
          clearRelevantStorage();
          return;
        }

        if (response.inError) return this._router.navigateByUrl("/user-profile");

        return this._loginService.doLogin(response.responseData.length > 0, user);
      },
    });
  }
}
